import fetchDownload from '../../../services/GetDownload';
import { DownloadFileFunc, FetchSummaryWithWeightsParams, Items } from '../../../types';
import { buildDownloadEndpoint, buildDownloadFiltersEndpoint } from '../../../utils/buildDownloadEndpoint';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import { getDownloadColumnsMap } from '../../../utils/buildDownloadEndpoint';
import { parseDownloadBlob } from './utils/parseDownloadBlob';

export type FetchDownloadDataParams = {
  extension?: 'tsv' | 'xlsx';
  columnVisibilityModel: Record<string, boolean>;
  view: string;
  source?: string;
  tab: string;
  searchItems: Items[];
  isAuthenticated: boolean;
  authorization: unknown;
  isEvidence: boolean;
  isMapping?: boolean;
  mappingContent?: DownloadFileFunc['mappingContent'];
  filters?: Record<string, unknown>;
  sourceWeights?: Array<{ name: string; score: number }>;
};

export async function fetchDownloadData({
  extension = 'tsv',
  columnVisibilityModel,
  view,
  source = '',
  tab,
  searchItems,
  isAuthenticated,
  authorization,
  isEvidence,
  isMapping = false,
  mappingContent,
  filters = {},
  sourceWeights = [],
}: FetchDownloadDataParams): Promise<Record<string, string>[]> {
  let url = buildDownloadFiltersEndpoint({
    extension,
    columnVisibilityModel,
    view,
    source,
    tab,
    searchItems,
    isEvidence,
    isMapping,
    mappingContent,
    filters,
    sourceWeights,
  });

  if (!isAuthenticated) {
    url += '&skey=unauth';
  }

  const blob = await fetchDownload(url, authorization);
  return parseDownloadBlob(blob, extension);
}

export async function fetchDownloadFile({
  extension,
  columnVisibilityModel,
  view,
  source = '',
  tab,
  searchItems,
  titleGrid,
  isAuthenticated,
  authorization,
  isEvidence,
  isMapping,
  mappingContent,
}: DownloadFileFunc & {
  filters?: Record<string, any>;
  totalResults?: number;
}): Promise<void> {
  let url = buildDownloadEndpoint({
    extension,
    columnVisibilityModel,
    view,
    source,
    tab,
    searchItems,
    isEvidence,
    isMapping,
    mappingContent,
  });

  if (!isAuthenticated) {
    url += '&skey=unauth';
  }

  const blob = await fetchDownload(url, authorization);
  const blobUrl = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = blobUrl;

  const fileName = `${view}_${titleGrid}_${tab}_${source}_${searchItems!
    .slice(0, 5)
    .map(i => i.id)
    .join('-')}.${extension}`;
  link.setAttribute('download', fileName);

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

//Replace the function above for this one in TR-116
export async function fetchDownloadFiltersFile({
  extension,
  columnVisibilityModel,
  view,
  source = '',
  tab,
  searchItems,
  titleGrid,
  isAuthenticated,
  authorization,
  isEvidence,
  isMapping,
  mappingContent,
  filters = {},
  totalResults = 0,
  filterValue,
  sourceWeights = [],
  columnLabels = {},
}: DownloadFileFunc & {
  filters?: Record<string, any>;
  totalResults?: number;
  sourceWeights?: Array<{ name: string; score: number }>;
}): Promise<void> {
  let url = buildDownloadFiltersEndpoint({
    extension,
    columnVisibilityModel,
    view,
    source,
    tab,
    searchItems,
    isEvidence,
    isMapping,
    mappingContent,
    filters,
    sourceWeights,
  });

  if (!isAuthenticated) {
    url += '&skey=unauth';
  }

  const dataBlob = await fetchDownload(url, authorization);
  const normalizedDataBlob = await normalizeDownloadHeaders({
    blob: dataBlob,
    extension,
    columnLabels,
  });

  const searchInfo = createSearchInfoContent({
    searchItems,
    view,
    isEvidence,
    source,
    tab,
    totalResults,
    filterArray: filterValue,
    columnVisibilityModel,
    isMapping,
    mappingContent,
    sourceWeights,
  });

  const zip = new JSZip();

  const maxIds = 5;
  const ids = searchItems!
    .slice(0, maxIds)
    .map(i => i.id)
    .join('-');
  const extra = searchItems!.length > maxIds ? `+${searchItems!.length - maxIds}` : '';

  const dataFileName = `search_result_${ids}${extra}.${extension}`;
  const infoFileName = `search_info_${ids}${extra}.txt`;

  const zipName = `search_${ids}${extra}.zip`;

  zip.file(dataFileName, normalizedDataBlob);
  zip.file(infoFileName, searchInfo);

  const zipBlob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });

  const zipUrl = window.URL.createObjectURL(zipBlob);
  const link = document.createElement('a');
  link.href = zipUrl;
  link.setAttribute('download', zipName);

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => {
    window.URL.revokeObjectURL(zipUrl);
  }, 1000);
}

function cleanHeaderCellValue(value: string): string {
  const trimmed = String(value).trim();
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
}

function applyHeaderLabel(value: string, displayLabel?: string): string {
  if (!displayLabel) return value;
  const trimmed = String(value).trim();
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) return `"${displayLabel}"`;
  if (trimmed.startsWith("'") && trimmed.endsWith("'")) return `'${displayLabel}'`;
  return displayLabel;
}

function buildBackendToUIKeyMap(): Map<string, string> {
  const map = new Map<string, string>();
  getDownloadColumnsMap().forEach((backendKey, uiKey) => {
    map.set(backendKey, uiKey);
  });
  return map;
}

async function normalizeDownloadHeaders({
  blob,
  extension,
  columnLabels,
}: {
  blob: Blob;
  extension: string;
  columnLabels?: Record<string, string>;
}): Promise<Blob> {
  if (!columnLabels || Object.keys(columnLabels).length === 0) return blob;

  const backendToUI = buildBackendToUIKeyMap();

  if (extension === 'tsv') {
    const raw = await blob.text();
    const lines = raw.split(/\r?\n/);
    if (lines.length === 0 || !lines[0]) return blob;

    const headers = lines[0].split('\t');
    const relabeledHeaders = headers.map(header => {
      const clean = cleanHeaderCellValue(header);
      const uiKey = backendToUI.get(clean) || clean;
      const display = columnLabels[uiKey] || columnLabels[clean];
      return applyHeaderLabel(header, display);
    });

    lines[0] = relabeledHeaders.join('\t');
    return new Blob([lines.join('\n')], { type: blob.type || 'text/tab-separated-values' });
  }

  if (extension === 'xlsx') {
    const workbook = XLSX.read(await blob.arrayBuffer(), { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) return blob;

    const worksheet = workbook.Sheets[firstSheetName];
    const rows = XLSX.utils.sheet_to_json<any[]>(worksheet, { header: 1 });
    if (!rows.length || !Array.isArray(rows[0])) return blob;

    rows[0] = rows[0].map((header: any) => {
      const key = String(header ?? '');
      const clean = cleanHeaderCellValue(key);
      const uiKey = backendToUI.get(clean) || clean;
      return columnLabels[uiKey] || columnLabels[clean] || header;
    });

    workbook.Sheets[firstSheetName] = XLSX.utils.aoa_to_sheet(rows);
    const output = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([output], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  }

  return blob;
}

function createSearchInfoContent({
  searchItems,
  view,
  isEvidence,
  source,
  tab,
  totalResults,
  filterArray,
  columnVisibilityModel,
  isMapping,
  mappingContent,
  sourceWeights = [],
}) {
  const formattedFilters = formatFiltersFromArray(filterArray);
  const ignoredSearchInfoColumns = new Set(['evidenceTimeline']);

  // Format search terms based on dataset type
  const formatSearchTerms = items => {
    if (!items || items.length === 0) return 'N/A';

    return items
      .map(item => {
        const dataset = item.dataset || view;
        switch (dataset) {
          case 'DISEASES':
            return `${item.name || 'N/A'} (CUI: ${item.id})`;
          case 'GENES':
            return `${item.symbol || 'N/A'} (${item.description || 'N/A'}, EntrezId: ${item.ncbiID || item.id})`;
          case 'VARIANTS':
            return `${item.strID || item.id} (Gene: ${item.geneSymbol || 'N/A'})`;
          case 'CHEMICALS':
            return `${item.chemPrefName || 'N/A'} (ID: ${item.chemID || item.id})`;
          default:
            return item.name || item.symbol || item.id || 'N/A';
        }
      })
      .join(', ');
  };

  return `SEARCH INFORMATION
==================

Download Date:       ${new Date().toLocaleString()}

SEARCH DETAILS
--------------
Search Terms:        ${formatSearchTerms(searchItems)}
Analysis Type:       ${view}
Data Type:           ${isEvidence ? 'Evidence' : 'Summary'}
Source:              ${source.replace('_', ' ')}
Tab:                 ${tab}
Total Results:       ${totalResults}

CUSTOM SCORE CONFIGURATION
--------------------------
${sourceWeights && sourceWeights.length > 0 
  ? `My Score Active:     Yes
Source Weights:      ${sourceWeights.map(sw => `${sw.name.replace('_', ' ')}: ${sw.score}`).join(', ')}`
  : 'My Score Active:     No'
}

FILTERS APPLIED
---------------
${formattedFilters}

DISPLAY SETTINGS
----------------
Visible Columns:     ${
    columnVisibilityModel
      ? Object.entries(columnVisibilityModel)
          .filter(([col, visible]) => visible && !ignoredSearchInfoColumns.has(col))
          .map(([col, _]) => col)
          .join(', ')
      : 'All'
  }

==================`;
}

function formatFiltersFromArray(filterArray) {
  if (!filterArray || filterArray.length === 0) return 'None';

  return filterArray
    .map(({ name, value, operator, emptiness }) => {
      if (name === 'evidenceTimeline') return null;
      if ((value === null || value === undefined || value === '') && !emptiness) return null;

      const parts: string[] = [];
      if (value !== null && value !== undefined && value !== '') {
        switch (operator) {
          case 'gt':
            parts.push(`${name}: > ${value}`);
            break;
          case 'lt':
            parts.push(`${name}: < ${value}`);
            break;
          case 'between':
            parts.push(
              Array.isArray(value)
                ? `${name}: between ${value[0]} and ${value[1]}`
                : `${name}: between ${value}`,
            );
            break;
          case 'equals':
          default:
            parts.push(`${name}: ${value}`);
        }
      }
      if (emptiness === 'not_empty') parts.push(`${name}: not empty`);
      if (emptiness === 'is_empty') parts.push(`${name}: is empty`);
      return parts.join(', ') || null;
    })
    .filter(Boolean)
    .join('\n');
}

export async function fetchSummaryWithWeights({
  source = 'ALL',
  disease,
  geneSymbol,
  pageNumber = 0,
  pageSize = 10,
  sourceWeights,
  authorization,
}: FetchSummaryWithWeightsParams): Promise<any> {
  const { API_URL } = await import('../../../components/Constants');

  const params = new URLSearchParams({
    source,
    page_number: pageNumber.toString(),
    page_size: pageSize.toString(),
  });

  if (disease) {
    params.append('disease', disease);
  }

  if (geneSymbol) {
    params.append('gene_symbol', geneSymbol);
  }

  if (sourceWeights.length > 0) {
    const weightsString = sourceWeights.map(sw => `${sw.name}:${sw.score}`).join(',');
    params.append('source_weights', weightsString);
  }

  const url = `${API_URL}api/gda/summary?${params.toString()}`;

  const headers = {
    'Content-Type': 'application/json',
    accept: 'application/json',
    Authorization: authorization,
  };

  const response = await fetch(url, { headers });
  const data = await response.json();

  return data;
}
